import {
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { IconSearch } from "@tabler/icons-react"
import { PaginationState } from "@tanstack/react-table"
import { CustomFieldValue } from "@tfg/db"
import { DEFAULT_PAGE_SIZE } from "@tfg/ui"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { BatchUpdateKeysType } from "@tfg/types/schemas/modules/traceability/dtos/find-empty-batch-operations"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { useActivitiesContext } from "~/components/contexts/useActivitiesContext"
import { BatchUpdateOperationsTable } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/BatchUpdateOperationsTable.tsx"
import { useBatchUpdateModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext.tsx"
import { useFindBatchOperationsHook } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useFindBatchOperations.hook.ts"
import { useGetOperations } from "~/utils/operations"

export interface BatchModalInfo {
  activityId: string
  operationId?: number
  requestId?: number | null
  update: BatchUpdateInfo
  label: string
  key: BatchUpdateKeysType
  displayActivityId?: string
}

export interface BatchUpdateInfo {
  productionSiteId?: string | null
  customFieldsValues?: Omit<CustomFieldValue, "operationId" | "id">[]
  reference?: string | null
  purchaseOrderRef?: string | null
  contactId: string
  doNotKnow?: boolean
}

export type BatchModalProps = {
  user: LibraryResponses.TFGUser
}

export function BatchModal() {
  const [searchProduct, setSearchProduct] = useState<string>("")
  const [debouncedProduct] = useDebouncedValue<string>(searchProduct, 600)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [isLoading, setIsLoading] = useState(false)
  const {
    setBatchModalOpen,
    batchModalInfo,
    batchModalOpen,
    setBatchModalInfo,
    setRowSelection,
  } = useBatchUpdateModalContext()

  const shouldFetch = batchModalOpen && batchModalInfo
  useEffect(() => {
    setBatchModalOpen(batchModalOpen)
  }, [batchModalOpen, setBatchModalOpen])

  const batchOperationsQuery = useFindBatchOperationsHook({
    update: {
      activityId: batchModalInfo?.activityId ?? null,
      operationId: batchModalInfo?.operationId ?? null,
      update: {
        productionSiteId: batchModalInfo?.update?.productionSiteId || null,
        reference: batchModalInfo?.update?.reference || "",
        purchaseOrderRef: batchModalInfo?.update?.purchaseOrderRef || "",
        customFieldsValues: batchModalInfo?.update?.customFieldsValues || [],
      },
      updateKey: batchModalInfo?.key ?? "",
    },
    searchProduct: debouncedProduct,
    fetchDataOptions: pagination,
    enabled: !!shouldFetch,
  })

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [debouncedProduct])

  const { t } = useTranslation(["traceability"])
  if (!batchModalInfo) return null
  return (
    <Modal
      opened={batchModalOpen}
      onClose={() => {
        setBatchModalOpen(false)
        setBatchModalInfo(null)
        setRowSelection({})
        setSearchProduct("")
      }}
      returnFocus={false}
      title={<Text size="lg">{t("batchModal.title")}</Text>}
      size="80vw"
    >
      <LoadingOverlay visible={isLoading} />
      <Stack pos="relative" w="100%">
        <Group />
        <DataFetchWrapper query={batchOperationsQuery}>
          {({ data }) => (
            <Stack mah="80vh">
              <Group justify="center" pos="relative">
                <TextInput
                  placeholder={t("batchModal.searchProduct")}
                  value={searchProduct}
                  size="xs"
                  fz="xs"
                  left={0}
                  pos="absolute"
                  leftSection={<IconSearch height={10} />}
                  onChange={(e) => setSearchProduct(e.currentTarget.value)}
                />
                <Text>
                  <Trans
                    t={t}
                    i18nKey="traceability:batchModal.info"
                    values={{
                      count: data.count,
                    }}
                    components={{
                      activity: (
                        <ActivityDisplay
                          activityId={
                            batchModalInfo.displayActivityId ??
                            batchModalInfo.activityId
                          }
                        />
                      ),
                      b: <b />,
                    }}
                  />
                </Text>
              </Group>
              <Divider />
              <BatchUpdateOperationsTable
                batchOperations={data}
                pagination={pagination}
                setPagination={setPagination}
                setIsLoading={setIsLoading}
              />
            </Stack>
          )}
        </DataFetchWrapper>
      </Stack>
    </Modal>
  )
}

interface Props {
  activityId: string
}

export const ActivityDisplay = ({ activityId }: Props) => {
  const activities = useActivitiesContext()
  const operations = useGetOperations()

  const activity = activities.find((activity) => activity.id === activityId)

  const operation = activity ? operations[activity.name] : undefined

  if (!operation) return null
  return (
    <>
      {operation.icon && <operation.icon size={20} />}
      {operation.name}
    </>
  )
}
